import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Collapse,
  IconButton,
} from "@mui/material";
import { ExpandMore as ExpandMoreIcon } from "@mui/icons-material";
import { FormattedMessage, useIntl } from "react-intl";
import { getAttendeeSpecialPackagesApi } from "../../utils/api/attendees";
import { SpecialPackage } from "../../redux/types/types";

interface SpecialPackagesListProps {
  attendeeId: number;
  classId?: number; // Optional - if provided, only show packages for this class
}

export default function SpecialPackagesList({
  attendeeId,
  classId,
}: SpecialPackagesListProps) {
  const intl = useIntl();
  const [specialPackages, setSpecialPackages] = useState<SpecialPackage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  const formatAmount = (amount: number) => {
    return (amount / 100).toFixed(2);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  useEffect(() => {
    const fetchSpecialPackages = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await getAttendeeSpecialPackagesApi(attendeeId);
        let packages = response.data;

        // Filter by classId if provided
        if (classId !== undefined) {
          packages = packages.filter((pkg: any) => pkg.classId === classId);
        }

        setSpecialPackages(packages);
      } catch (error) {
        console.error("Failed to fetch special packages:", error);
        setError(
          intl.formatMessage({ id: "attendee.specialPackages.fetchError" })
        );
      } finally {
        setLoading(false);
      }
    };

    fetchSpecialPackages();
  }, [attendeeId, classId, intl]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={1}>
        <CircularProgress size={16} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 1 }}>
        {error}
      </Alert>
    );
  }

  if (specialPackages.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
          "&:hover": { bgcolor: "action.hover" },
          borderRadius: 1,
          p: 0.5,
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
          <FormattedMessage
            id="attendee.specialPackages.title"
            defaultMessage="Special Packages ({count})"
            values={{ count: specialPackages.length }}
          />
        </Typography>
        <IconButton
          size="small"
          sx={{
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s",
          }}
        >
          <ExpandMoreIcon fontSize="small" />
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {specialPackages.map((pkg) => (
            <Chip
              key={pkg.id}
              label={`${pkg.specialType} (${formatAmount(pkg.amount)} ${
                pkg.currency
              }) - ${formatDate(pkg.purchasedAt)}`}
              size="small"
              variant="outlined"
              color="primary"
              sx={{ fontSize: "0.75rem" }}
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}
